import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, MessageCircle, X } from "lucide-react";
import { LINKS, NAV_ITEMS, scrollToSection } from "@/lib/site";

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (e, href) => {
    setOpen(false);
    scrollToSection(e, href);
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,box-shadow] duration-300 ${
        scrolled || open
          ? "border-b border-white/10 bg-black/92 backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-[76px] max-w-7xl items-center justify-between gap-4 px-5 lg:px-10">
        <a
          href="#home"
          onClick={(e) => handleNav(e, "#home")}
          data-testid="nav-brand-link"
          className="flex shrink-0 items-center"
          aria-label="Jewish Global Wire — Home"
        >
          <img
            src="/jgw-logo.jpeg"
            alt="Jewish Global Wire"
            className="h-12 w-12 rounded-full object-cover ring-1 ring-white/25"
          />
        </a>

        <nav className="hidden items-center gap-8 lg:flex" data-testid="nav-desktop-menu">
          {NAV_ITEMS.map((item) => (
            <a
              key={item.href}
              href={item.href}
              onClick={(e) => handleNav(e, item.href)}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}-link`}
              className="text-sm font-semibold text-silver transition-colors duration-200 hover:text-white"
            >
              {item.label}
            </a>
          ))}
          <a
            href={LINKS.community}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-join-button"
            className="inline-flex items-center gap-2 rounded-xl bg-whatsapp px-5 py-2.5 text-sm font-bold text-white transition-colors duration-200 hover:bg-whatsapp-dark"
          >
            <MessageCircle size={16} strokeWidth={2.2} />
            Join JGW
          </a>
        </nav>

        <div className="flex items-center gap-2 lg:hidden">
          <a
            href={LINKS.community}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-mobile-join-button"
            className="inline-flex items-center gap-1.5 rounded-xl bg-whatsapp px-4 py-2 text-xs font-bold text-white transition-colors duration-200 hover:bg-whatsapp-dark"
          >
            <MessageCircle size={14} strokeWidth={2.2} />
            Join JGW
          </a>
          <button
            type="button"
            aria-label="Toggle menu"
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-white transition-colors duration-200 hover:bg-white/10"
          >
            {open ? <X size={20} strokeWidth={2} /> : <Menu size={20} strokeWidth={2} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="overflow-hidden border-t border-white/10 bg-black/95 backdrop-blur-xl lg:hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col px-5 py-3">
              {NAV_ITEMS.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={(e) => handleNav(e, item.href)}
                  data-testid={`nav-mobile-${item.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                  className="border-b border-white/5 py-3.5 text-sm font-semibold text-silver-light transition-colors duration-200 last:border-0 hover:text-white"
                >
                  {item.label}
                </a>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
